<template>
  <footer class="bg-surface-grey-light pb-20 md:pb-0" v-if="!isEmbed()">
    <BaseContainer>
      <div
        class="flex flex-col items-center md:items-start text-center md:text-left md:grid md:grid-cols-4 lg:grid-cols-5 gap-8 py-10 md:py-20"
      >
        <div class="lg:col-span-2">
          <div class="flex-shrink-0 flex mx-auto md:mx-0 items-center w-50 h-12">
            <GlobalCustomerLogo :useFooterLogo="true" />
        </div>

          <div class="text-headings text-center md:text-left">
            <p class="font-bold mb-2 mt-8">{{ company }}</p>
            <p>{{ address }}</p>
          </div>
        </div>

        <div v-for="menu in menus" :key="menu.title">
          <h4 class="font-semibold text-headings mb-4" v-if="menu.links.length > 0">{{ $t(menu.title) }}</h4>
          <ul class="text-headings space-y-2">
            <li v-for="(link, index) in menu.links" :key="index">
              <RouterLink :to="link.url" class="hover:underline">{{ $t(link.name) }}</RouterLink>
            </li>
          </ul>
        </div>
        <!-- On language select, call setEventDescriptionsFromLanguage -->
        <InputSelect
          v-if="showLanguageSelect"
          class="w-52 md:w-full"
          v-model="$i18n.locale"
          @change="useCheckoutStore().setEventDescriptionsFromLanguage( false, $i18n.locale )"
          :options="languageOptions"
          name="language"
          id="language-select"
          :placeholder="$t('globals.change_language')"
          :hide-title="false"
        />
      </div>
    </BaseContainer>

    <!-- CTA Section -->
    <div
      v-if="cta"
      class="bg-surface-grey mx-4 md:mx-0 rounded-md md:rounded-none"
    >
      <BaseContainer>
        <div
          class="flex flex-col md:flex-row justify-between items-center py-6 md:py-12 gap-6 md:gap-0"
        >
          <div>
            <h3 class="h4">{{ $t(cta.title) }}</h3>

            <ul
              class="flex flex-col md:flex-row gap-3 md:gap-6 md:opacity-75 mt-3"
            >
              <li
                v-for="usp in cta.usps"
                :key="usp"
                class="text-headings flex items-center text-sm font-semibold"
              >
                <CheckmarkIcon class="h-3 w-3 mr-2" />
                {{ $t(usp) }}
              </li>
            </ul>
          </div>
          <BaseButton
            v-if="cta.button"
            class="w-full md:w-auto"
            :href="cta.button.url"
            color="accent"
          >
            {{ $t(cta.button.text) }}
            <ArrowRightIcon class="fill-current h-4 w-4 ml-3" />
          </BaseButton>
        </div>
      </BaseContainer>
    </div>

    <!-- Bottom Footer -->
    <div v-if="logoFooter">
      <BaseContainer>
        <div class="flex flex-col md:flex-row justify-between py-8 gap-8">
          <SafeticketIcon class="h-5 w-auto fill-headings" />

          <div class="flex flex-col md:flex-row items-center gap-8 text-sm">
            <a
              v-for="link in logoFooter.links"
              :key="link.name"
              :href="link.url"
              :target="link.target"
              :rel="link.target === '_blank' ? 'noopener noreferrer' : undefined"
              class="text-headings hover:underline"
            >
              {{ $t(link.name) }}
            </a>
          </div>
        </div>
      </BaseContainer>
    </div>

    <!-- Arrangør login section -->
    <!-- <div v-if="bottomFooter" class="border-t border-surface-grey py-6">
      <BaseContainer>
        <div class="flex flex-col md:flex-row items-center gap-6">
          <a
            v-for="link in bottomFooter.links"
            :key="link.name"
            :href="link.url"
          >
            {{ $t(link.name) }}
          </a>
        </div>
      </BaseContainer>
    </div> -->
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCheckoutStore } from '@/stores/checkoutStore';
import { useGeneralStore } from '@/stores/general';
import { generateAddressStringDotSeparator } from '@/utils/addressUtils';
import { getLabelForLanguage } from '@/utils/getLabelForLanguage';
import { isEmbed } from '@/utils/isEmbed';

import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
import CheckmarkIcon from '../icons/CheckmarkIcon.vue';
import SafeticketIcon from '../icons/SafeticketIcon.vue';
import BaseButton from '../ui/BaseButton.vue';
import BaseContainer from '../ui/BaseContainer.vue';
import InputSelect from '../ui/inputs/InputSelect.vue';
import GlobalCustomerLogo from './GlobalCustomerLogo.vue';

const { t: $t } = useI18n();

const generalStore = useGeneralStore();

// Menu footer
const company = computed(() => generalStore.customer().name);
const address = computed(() => generateAddressStringDotSeparator(generalStore.customer()));

const menus = computed(() => [
  {
    title: 'globals.ticket_buyer',
    links: [
      { 
        name: 'globals.resend_tickets', 
        url: '/resend-tickets',
        visible: true 
      },
      { 
        name: 'globals.check_gift_card', 
        url: '/get-gift-card-balance', 
        visible: generalStore.customer().giftcards 
      },
      { 
        name: 'globals.cancel_tickets', 
        url: '/cancel-order', 
        visible: generalStore.customer().ticketbuyer_can_cancel_tickets 
      },
      { 
        name: 'globals.transfer_voucher', 
        url: '/vouchercode-count-transfer', 
        visible: generalStore.customer().vouchercode_count_transfer 
      },
    ].filter(link => link.visible === undefined || Boolean(link.visible)), // Filter out links where visible is explicitly false
  },
  {
    title: 'globals.contact',
    links: [
      { name: 'globals.contact', url: '/contact' },
      { name: 'globals.terms', url: '/terms' },
      // { name: 'globals.cookies', url: '/cookies' },
    ],
  },
]);

// Updated the language-select to only be visible if generalStore.customer().localization is 1
const showLanguageSelect = computed(() => generalStore.customer().localization === 1);

// Made the options in language-select a computed value
const languageOptions = computed(() => {
  const languages = generalStore.languages() || [];  
  return languages.map(language => ({
    value: language,
    label: getLabelForLanguage(language), 
  }));
});

// CTA footer 
const cta = computed(() => {
  if (generalStore.customer().cashflow === 0) {
    return {
      title: 'globals.cta_title',
      usps: ['globals.cta_unique_selling_point_low_fee', 'globals.cta_unique_selling_point_support'],
      button: {
        text: 'globals.cta_button_text',
        url: 'https://www.safeticket.dk',
      },
    };
  }
  return null;
});

// Logo footer
const logoFooter = computed(() => ({
  links: [
    { name: 'globals.organizer_login', 
      url: '/admin/',
      visible: generalStore.customer().login_link 
    },
    { name: 'Billetsystem', 
      url: 'https://www.safeticket.dk',
      visible: generalStore.customer().cashflow === 0,
      target: '_blank'
    },
    { name: 'Tilmeldingssystem', 
      url: 'https://www.safeticket.dk/tilmeldingssystem/',
      visible: generalStore.customer().cashflow === 0,
      target: '_blank'
    },
  ].filter(link => link.visible === undefined || Boolean(link.visible)),
}));

// bottom footer
const bottomFooter = computed(() => {
  // Ensure bottomFooter always returns an object with a links property
  return {
    links: [
      { name: 'globals.organizer_login', url: generalStore.customer().login_link },
    ].filter(link => link.url), // Filter out links with falsy URLs
  };
});
</script>

